/* Rich Girl on Vacation — theme.js
 * Vanilla JS. No frameworks.
 * Handles: marquee duplication for seamless loop, mobile nav toggle,
 * variant selection on product page, cart line quantity updates.
 */

(() => {
  const ready = (fn) =>
    document.readyState !== 'loading' ? fn() : document.addEventListener('DOMContentLoaded', fn);

  let revealObserver = null;

  ready(() => {
    initReveal();
    initMarquees();
    initMobileNav();
    initMegaMenu();
    initVariantSelectors();
    initCartUpdates();
    initCartDrawer();
    initSearchModal();
    initCategoryNav();
    initLoadMore();
    initLocaleForm();
    initScrollToTop();
    initCardRotation();
    initNationalityFilter();
    initLogoCycle();
    initAnnouncementRotator();
    initRecentlyViewed();
    initExitIntent();
    initProductGallery();
    initStickyATC();
    initSizeGuide();
  });

  /* Multi-image product gallery — click thumb to swap main image */
  function initProductGallery() {
    document.querySelectorAll('[data-gallery]').forEach((g) => {
      const main = g.querySelector('[data-main-img]');
      const mainWrap = g.querySelector('.product__main-img');
      const thumbs = g.querySelectorAll('[data-thumb]');
      if (!main || !thumbs.length) return;
      thumbs.forEach((t) => {
        t.addEventListener('click', () => {
          if (t.classList.contains('is-active')) return;
          thumbs.forEach((x) => x.classList.remove('is-active'));
          t.classList.add('is-active');
          if (mainWrap) mainWrap.classList.add('is-changing');
          const newSrc = t.dataset.imgSrc;
          const newAlt = t.dataset.imgAlt || '';
          // Preload to avoid flicker
          const pre = new Image();
          pre.onload = () => {
            main.src = newSrc;
            main.alt = newAlt;
            // Match the box to the swapped image's real ratio so it never reflows/crops
            if (pre.naturalWidth) { main.width = pre.naturalWidth; main.height = pre.naturalHeight; }
            requestAnimationFrame(() => {
              if (mainWrap) mainWrap.classList.remove('is-changing');
            });
          };
          pre.src = newSrc;
        });
      });
    });
  }

  /* Sticky Add-to-Cart bar — show when main button scrolls out of view */
  function initStickyATC() {
    const bar = document.querySelector('[data-sticky-atc]');
    const mainBtn = document.querySelector('[data-add-btn]');
    if (!bar || !mainBtn) return;
    bar.hidden = false; // reveal so the slide transition works (but is-visible class still controls actual visibility)

    if (!('IntersectionObserver' in window)) {
      // fallback: always show
      bar.classList.add('is-visible');
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) bar.classList.remove('is-visible');
        else bar.classList.add('is-visible');
      });
    }, { rootMargin: '-100px 0px 0px 0px', threshold: 0 });
    observer.observe(mainBtn);

    // Sticky button proxies the main form submit (so all variant logic stays in one place)
    const stickyBtn = bar.querySelector('[data-sticky-add]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        const form = mainBtn.closest('form');
        if (form) form.requestSubmit ? form.requestSubmit(mainBtn) : mainBtn.click();
      });
    }

    // Keep sticky price in sync with variant changes
    const priceEl = bar.querySelector('[data-sticky-price]');
    const productPrice = document.querySelector('[data-product-price]');
    if (priceEl && productPrice) {
      const sync = () => { priceEl.textContent = productPrice.textContent.trim(); };
      // Watch for changes — variant click handler replaces priceEl text
      new MutationObserver(sync).observe(productPrice, { childList: true, characterData: true, subtree: true });
    }
  }

  /* Size guide modal — open/close + tabs */
  function initSizeGuide() {
    const modal = document.querySelector('[data-size-guide]');
    if (!modal) return;
    const tabs = modal.querySelectorAll('[data-tab]');
    const panes = modal.querySelectorAll('[data-pane]');

    const open = () => {
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => modal.classList.add('is-open'));
      document.body.classList.add('size-guide-open');
    };
    const close = () => {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('size-guide-open');
      setTimeout(() => { modal.hidden = true; }, 200);
    };

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-size-guide-toggle]')) { e.preventDefault(); open(); return; }
      if (e.target.closest('[data-size-guide-close]')) { e.preventDefault(); close(); return; }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.hidden) close();
    });

    tabs.forEach((t) => {
      t.addEventListener('click', () => {
        tabs.forEach((x) => x.classList.remove('is-active'));
        t.classList.add('is-active');
        const target = t.dataset.tab;
        panes.forEach((p) => p.classList.toggle('is-active', p.dataset.pane === target));
      });
    });
  }

  /* =========================================================
   * Recently viewed — hydrates the section from localStorage by
   * fetching each /products/{handle}.js and rendering minimal cards.
   * ========================================================= */
  function initRecentlyViewed() {
    const root = document.querySelector('[data-recently-viewed]');
    if (!root) return;
    const grid = root.querySelector('[data-recently-viewed-grid]');
    if (!grid) return;

    let handles = [];
    try { handles = JSON.parse(localStorage.getItem('rgov-recently-viewed') || '[]'); } catch (e) {}
    if (!Array.isArray(handles) || handles.length === 0) return;

    const currentMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (currentMatch) handles = handles.filter((h) => h !== currentMatch[1]);

    const max = parseInt(root.dataset.max || '8', 10);
    handles = handles.slice(0, max);
    if (!handles.length) return;

    Promise.all(handles.map((h) =>
      fetch(`/products/${encodeURIComponent(h)}.js`, { headers: { 'Accept': 'application/json' } })
        .then((r) => r.ok ? r.json() : null)
        .catch(() => null)
    )).then((products) => {
      const valid = products.filter(Boolean);
      if (!valid.length) return;
      grid.innerHTML = valid.map(renderRecentlyViewedCard).join('');
      root.hidden = false;
      observeReveals(root);
    });
  }

  function renderRecentlyViewedCard(p) {
    const img = p.featured_image
      ? `<img src="${escapeHtml(p.featured_image)}" alt="${escapeHtml(p.title)}" loading="lazy" width="600" height="750">`
      : `<div style="width:100%;height:100%;background:hsl(var(--secondary));"></div>`;
    const onSale = p.compare_at_price && p.compare_at_price > p.price;
    const badge = onSale ? '<span class="product-card__badge">Sale</span>' : '';
    const price = onSale
      ? `<del>${formatMoney(p.compare_at_price)}</del> ${formatMoney(p.price)}`
      : formatMoney(p.price);
    return `<a href="${escapeHtml(p.url)}" class="product-card" data-reveal>
      <div class="product-card__media">${img}${badge}</div>
      <div class="product-card__info">
        <h3 class="product-card__title">${escapeHtml(p.title)}</h3>
        <div class="product-card__price">${price}</div>
      </div>
    </a>`;
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    return '$' + (cents / 100).toFixed(2);
  }

  /* =========================================================
   * Exit-intent popup — fires on intent-to-leave, collects
   * email via Shopify customer form, displays discount code.
   *
   * Triggers:
   *   Desktop: cursor leaves viewport via the top edge
   *   Mobile : after 25s on page, scroll-up at any depth
   *
   * Once shown (or completed), suppressed for 7 days via
   * localStorage. Skipped on cart/checkout/account templates.
   * ========================================================= */
  function initExitIntent() {
    const popup = document.querySelector('[data-exit-intent]');
    if (!popup) return;

    const SUPPRESS_KEY = 'rgov-exit-intent-suppressed-until';
    const ARM_MS  = (parseInt(popup.dataset.armSeconds, 10) || 6) * 1000;
    const MOBILE_DELAY_MS = 25 * 1000;
    const showOnMobile = popup.dataset.showOnMobile === '1';
    const code = popup.dataset.discountCode || '';
    const pct  = popup.dataset.discountPercent || '';

    // Skip if recently suppressed
    try {
      const until = parseInt(localStorage.getItem(SUPPRESS_KEY), 10);
      if (until && Date.now() < until) return;
    } catch (e) {}

    const isMobile = window.matchMedia('(max-width: 767px), (pointer: coarse)').matches;
    if (isMobile && !showOnMobile) return;

    const pitchEl   = popup.querySelector('[data-exit-intent-pitch]');
    const successEl = popup.querySelector('[data-exit-intent-success]');
    const formEl    = popup.querySelector('[data-exit-intent-form]');
    const emailEl   = popup.querySelector('[data-exit-intent-email]');
    const submitBtn = popup.querySelector('[data-exit-intent-submit]');
    const copyBtn   = popup.querySelector('[data-exit-intent-copy]');
    const copyLbl   = popup.querySelector('[data-exit-intent-copy-label]');

    let armed = false;
    let shown = false;
    setTimeout(() => { armed = true; }, ARM_MS);

    const suppress = (days = 7) => {
      try {
        localStorage.setItem(SUPPRESS_KEY, String(Date.now() + days * 86400 * 1000));
      } catch (e) {}
    };

    const show = () => {
      if (shown) return;
      shown = true;
      popup.hidden = false;
      popup.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => popup.classList.add('is-open'));
      document.body.classList.add('exit-intent-open');
      cleanupTriggers();
      // Suppress on first show for 7 days regardless of action
      suppress(7);
    };

    const close = () => {
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('exit-intent-open');
      setTimeout(() => { popup.hidden = true; }, 250);
    };

    /* Desktop trigger: leaving viewport via the top */
    const onMouseOut = (e) => {
      if (!armed) return;
      if (e.relatedTarget !== null) return;
      if (e.clientY > 0) return;
      show();
    };

    /* Mobile trigger: scroll-up after dwell time */
    let lastScrollY = window.scrollY;
    let mobileEligible = false;
    setTimeout(() => { mobileEligible = true; }, MOBILE_DELAY_MS);
    const onScroll = () => {
      const y = window.scrollY;
      if (mobileEligible && y < lastScrollY - 60 && y > 50) show();
      lastScrollY = y;
    };

    const cleanupTriggers = () => {
      document.removeEventListener('mouseout', onMouseOut);
      window.removeEventListener('scroll', onScroll);
    };

    if (isMobile) {
      window.addEventListener('scroll', onScroll, { passive: true });
    } else {
      document.addEventListener('mouseout', onMouseOut);
    }

    /* Close handlers */
    popup.addEventListener('click', (e) => {
      if (e.target.closest('[data-exit-intent-close]')) {
        e.preventDefault();
        close();
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !popup.hidden) close();
    });

    /* Form submit — optimistic UI + AJAX customer creation */
    if (formEl) {
      formEl.addEventListener('submit', (e) => {
        e.preventDefault();
        if (!emailEl || !emailEl.value || !emailEl.checkValidity()) {
          if (emailEl) emailEl.reportValidity();
          return;
        }

        // Disable the button to prevent double-submit
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.classList.add('is-loading');
        }

        // Reveal success state immediately (optimistic)
        if (pitchEl)   pitchEl.hidden = true;
        if (successEl) successEl.hidden = false;
        suppress(180); // long suppress for converted users

        // POST in background to /contact (Shopify customer form handler)
        const fd = new FormData(formEl);
        // Ensure the form_type is present (Shopify expects it)
        if (!fd.get('form_type')) fd.append('form_type', 'customer');
        if (!fd.get('utf8'))      fd.append('utf8', '✓');

        fetch('/contact', {
          method: 'POST',
          body: fd,
          headers: { 'Accept': 'text/html' },
          credentials: 'same-origin',
          redirect: 'follow'
        }).then((resp) => {
          // Shopify returns 302 → 200 (the redirect target). We don't read the body.
          // Fire a custom event so analytics can hook in.
          document.dispatchEvent(new CustomEvent('rgov:newsletter-signup', {
            detail: { email: emailEl.value, source: 'exit-intent', code, percent: pct }
          }));
        }).catch((err) => {
          console.warn('Newsletter background submit failed', err);
        });
      });
    }

    /* Copy-to-clipboard */
    if (copyBtn) {
      copyBtn.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(code);
          if (copyLbl) {
            const original = copyLbl.textContent;
            copyLbl.textContent = 'Copied!';
            copyBtn.classList.add('is-copied');
            setTimeout(() => {
              copyLbl.textContent = original;
              copyBtn.classList.remove('is-copied');
            }, 1800);
          }
        } catch (err) {
          // Fallback: select the code text so the user can copy manually
          const codeEl = popup.querySelector('[data-exit-intent-code-text]');
          if (codeEl && window.getSelection) {
            const range = document.createRange();
            range.selectNodeContents(codeEl);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
          }
        }
      });
    }
  }

  /* Logo color-cycling on hover (5 colors, 2.5s cycle, intense glow + pulse) */
  function initLogoCycle() {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const logo = document.querySelector('[data-logo]');
    const text = document.querySelector('[data-logo-text]');
    if (!logo || !text) return;
    const colors = [
      'hsl(0, 84%, 40%)',     // deep red
      'hsl(35, 100%, 35%)',   // burnt orange/amber
      'hsl(142, 76%, 30%)',   // forest green
      'hsl(280, 70%, 40%)',   // deep purple
      'hsl(210, 100%, 35%)',  // royal blue
    ];
    let lastIdx = 0;
    let timer = null;
    const apply = (i) => {
      const c = colors[i];
      text.style.color = c;
      // Single small drop-shadow — filter:drop-shadow follows the letter
      // alpha mask, so the halo hugs each letterform. Keeping the blur
      // small (3px) ensures adjacent letters' shadows don't merge into
      // the bounding-box colored block we used to see.
      text.style.textShadow = 'none';
      text.style.filter = `drop-shadow(0 0 3px ${c})`;
      lastIdx = i;
    };
    const reset = () => {
      text.style.color = '';
      text.style.textShadow = '';
      text.style.filter = '';
    };
    logo.addEventListener('mouseenter', () => {
      const start = (lastIdx + 1) % colors.length;
      apply(start);
      if (reduce) return;
      timer = setInterval(() => apply((lastIdx + 1) % colors.length), 2500);
    });
    logo.addEventListener('mouseleave', () => {
      if (timer) { clearInterval(timer); timer = null; }
      reset();
    });
  }

  /* Announcement bar — rotate messages with fade transition. */
  function initAnnouncementRotator() {
    const bar = document.querySelector('[data-announcement-bar]');
    if (!bar) return;
    const msgs = bar.querySelectorAll('[data-announcement-msg]');
    if (msgs.length < 2) return;
    let active = 0;
    msgs[0].classList.add('is-active');
    setInterval(() => {
      msgs[active].classList.remove('is-active');
      active = (active + 1) % msgs.length;
      msgs[active].classList.add('is-active');
    }, 3000);
  }

  function initNationalityFilter() {
    const root = document.querySelector('[data-nationality-filter]');
    if (!root) return;
    const sheet = root.querySelector('[data-nationality-sheet]');
    const mobileBtn = root.querySelector('[data-nationality-mobile]');

    let ticking = false;
    const update = () => {
      root.classList.toggle('is-visible', window.scrollY > 300);
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();

    if (mobileBtn && sheet) {
      const open = () => {
        sheet.hidden = false;
        requestAnimationFrame(() => sheet.classList.add('is-open'));
        document.body.classList.add('nationality-sheet-open');
      };
      const close = () => {
        sheet.classList.remove('is-open');
        document.body.classList.remove('nationality-sheet-open');
        setTimeout(() => { sheet.hidden = true; }, 250);
      };
      mobileBtn.addEventListener('click', open);
      sheet.addEventListener('click', (e) => {
        if (e.target.closest('[data-nationality-close]')) close();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !sheet.hidden) close();
      });
    }
  }

  function initCardRotation() {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    document.querySelectorAll('[data-card-rotate]').forEach((card, cardIdx) => {
      const imgs = card.querySelectorAll('[data-rotate-img]');
      const dots = card.querySelectorAll('[data-rotate-dots] > .category-card__dot');
      if (imgs.length < 2) return;
      let active = 0;
      // Stagger initial start so cards don't all flip at once
      setTimeout(() => {
        setInterval(() => {
          imgs[active].classList.remove('is-active');
          if (dots[active]) dots[active].classList.remove('is-active');
          active = (active + 1) % imgs.length;
          imgs[active].classList.add('is-active');
          if (dots[active]) dots[active].classList.add('is-active');
        }, 3500);
      }, cardIdx * 400);
    });
  }

  function initReveal() {
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-visible'));
      return;
    }
    revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { rootMargin: '120px 0px 120px 0px', threshold: 0 });
    document.querySelectorAll('[data-reveal]').forEach((el) => revealObserver.observe(el));

    // Safety net: NEVER let the reveal animation trap content (a hidden product grid = critical).
    // After 2s, force EVERY reveal element to its visible end-state via inline styles. This covers
    // both a flaky/missed observer AND the stuck case where an element has .is-visible but its
    // opacity transition never applied — inline styles win over any cascade/transition quirk.
    setTimeout(() => {
      document.querySelectorAll('[data-reveal]').forEach((el) => {
        el.classList.add('is-visible');
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
    }, 2000);
  }

  function observeReveals(scope) {
    if (!revealObserver) return;
    (scope || document).querySelectorAll('[data-reveal]:not(.is-visible)').forEach((el) => {
      revealObserver.observe(el);
    });
  }

  function initScrollToTop() {
    const btn = document.querySelector('[data-scroll-to-top]');
    if (!btn) return;
    const threshold = 400;
    let ticking = false;
    const update = () => {
      btn.classList.toggle('is-visible', window.scrollY > threshold);
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    btn.addEventListener('click', () => {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    });
    update();
  }

  function initLocaleForm() {
    document.querySelectorAll('[data-locale-form]').forEach((form) => {
      const trigger = form.querySelector('[data-locale-trigger]');
      const dropdown = form.querySelector('[data-locale-dropdown]');
      if (!trigger || !dropdown) return;

      const close = () => {
        dropdown.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
      };
      const open = () => {
        // Close other locale dropdowns first
        document.querySelectorAll('[data-locale-dropdown]').forEach((d) => {
          if (d !== dropdown) d.hidden = true;
        });
        document.querySelectorAll('[data-locale-trigger]').forEach((t) => {
          if (t !== trigger) t.setAttribute('aria-expanded', 'false');
        });
        dropdown.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
      };

      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        dropdown.hidden ? open() : close();
      });
    });

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-locale-form]')) return;
      document.querySelectorAll('[data-locale-dropdown]').forEach((d) => { d.hidden = true; });
      document.querySelectorAll('[data-locale-trigger]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      document.querySelectorAll('[data-locale-dropdown]').forEach((d) => { d.hidden = true; });
      document.querySelectorAll('[data-locale-trigger]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
    });
  }

  function initLoadMore() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-load-more]');
      if (!btn) return;
      e.preventDefault();
      const url = btn.dataset.nextUrl;
      const sectionId = btn.dataset.sectionId;
      const wrap = btn.closest('[data-load-more-wrap]');
      const section = btn.closest('[data-featured-products]');
      const grid = section ? section.querySelector('[data-products-container]') : null;
      if (!url || !sectionId || !grid || !wrap) return;

      const original = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Loading…';

      try {
        const sep = url.includes('?') ? '&' : '?';
        const fetchUrl = withThemeContext(url + sep + 'section_id=' + encodeURIComponent(sectionId));
        const res = await fetch(fetchUrl, { headers: { 'Accept': 'text/html' } });
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newGrid = doc.querySelector('[data-products-container]');
        const newWrap = doc.querySelector('[data-load-more-wrap]');
        if (newGrid) {
          Array.from(newGrid.children).forEach((child) => {
            grid.appendChild(child);
            observeReveals(child);
          });
        }
        if (newWrap) {
          wrap.replaceWith(newWrap);
        } else {
          wrap.remove();
        }
      } catch (err) {
        console.error('load more failed', err);
        btn.disabled = false;
        btn.textContent = original;
      }
    });
  }

  function initCategoryNav() {
    const scroll = document.querySelector('[data-category-scroll]');
    if (!scroll) return;
    const active = scroll.querySelector('.is-active');
    if (active) {
      // Center the active item without animating the page
      const target = active.offsetLeft - (scroll.clientWidth / 2) + (active.clientWidth / 2);
      scroll.scrollLeft = Math.max(0, target);
    }
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  }

  /* When previewing an unpublished theme, AJAX calls that hit the
   * Section Rendering API need the preview_theme_id query param,
   * otherwise Shopify renders sections from the *published* theme. */
  function withThemeContext(url) {
    try {
      const previewId = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (!previewId) return url;
      const sep = url.includes('?') ? '&' : '?';
      return url + sep + 'preview_theme_id=' + encodeURIComponent(previewId);
    } catch (e) { return url; }
  }

  function initSearchModal() {
    const modal = document.querySelector('[data-search-modal]');
    if (!modal) return;
    const input = modal.querySelector('[data-search-input]');
    const empty = modal.querySelector('[data-search-empty]');
    const results = modal.querySelector('[data-search-results]');
    const sugWrap = modal.querySelector('[data-search-suggestions]');
    const prodWrap = modal.querySelector('[data-search-products]');
    const clearBtn = modal.querySelector('[data-search-clear]');
    const goLink = modal.querySelector('[data-search-go]');
    const goTerm = modal.querySelector('[data-search-go-term]');
    const form = modal.querySelector('[data-search-form]');

    const open = () => {
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => modal.classList.add('is-open'));
      document.body.classList.add('search-modal-open');
      setTimeout(() => input && input.focus(), 80);
    };
    const close = () => {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('search-modal-open');
      setTimeout(() => {
        modal.hidden = true;
        if (input) input.value = '';
        if (clearBtn) clearBtn.hidden = true;
        renderResults('');
      }, 200);
    };

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-search-toggle]')) { e.preventDefault(); open(); return; }
      if (e.target.closest('[data-search-close]')) { e.preventDefault(); close(); }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.hidden) close();
    });

    let debounce = null;
    if (input) {
      input.addEventListener('input', () => {
        if (clearBtn) clearBtn.hidden = !input.value;
        clearTimeout(debounce);
        debounce = setTimeout(() => renderResults(input.value), 200);
      });
    }

    if (clearBtn) clearBtn.addEventListener('click', () => {
      input.value = ''; clearBtn.hidden = true; renderResults(''); input.focus();
    });

    if (form) {
      form.addEventListener('submit', (e) => {
        const q = (input.value || '').trim();
        if (!q) { e.preventDefault(); return; }
        // Native form submit posts to /search?q=…
      });
    }

    if (goLink) {
      goLink.addEventListener('click', (e) => {
        const q = (input.value || '').trim();
        if (!q) { e.preventDefault(); return; }
        goLink.href = '/search?q=' + encodeURIComponent(q);
      });
    }

    // Click on a suggestion fills the input
    modal.addEventListener('click', (e) => {
      const sug = e.target.closest('[data-suggestion]');
      if (sug) {
        input.value = sug.dataset.suggestion;
        if (clearBtn) clearBtn.hidden = false;
        renderResults(input.value);
        input.focus();
      }
    });

    async function renderResults(query) {
      const q = (query || '').trim();
      if (!q) {
        if (empty) empty.hidden = false;
        if (results) results.hidden = true;
        if (goLink) goLink.hidden = true;
        return;
      }
      if (empty) empty.hidden = true;
      if (goTerm) goTerm.textContent = q;
      if (goLink) { goLink.hidden = false; goLink.href = '/search?q=' + encodeURIComponent(q); }
      if (results) results.hidden = false;

      try {
        const url = '/search/suggest.json?q=' + encodeURIComponent(q)
          + '&resources[type]=product,query'
          + '&resources[limit]=6'
          + '&resources[options][unavailable_products]=hide';
        const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
        const data = await res.json();
        const r = (data.resources && data.resources.results) || {};

        const queries = r.queries || [];
        sugWrap.innerHTML = queries.length
          ? queries.map(qq => {
              const txt = qq.styled_text || escapeHtml(qq.text);
              const raw = escapeHtml(qq.text);
              return '<li><button type="button" data-suggestion="' + raw + '">' + txt + '</button></li>';
            }).join('')
          : '<li class="muted">No suggestions found</li>';

        const products = r.products || [];
        prodWrap.innerHTML = products.length
          ? products.map(p => {
              const imgHtml = p.image
                ? '<img src="' + escapeHtml(p.image) + '" alt="' + escapeHtml(p.title) + '" loading="lazy">'
                : '';
              const price = p.price != null ? escapeHtml(p.price) : '';
              return '<li><a href="' + escapeHtml(p.url) + '">'
                + '<div class="pimg">' + imgHtml + '</div>'
                + '<div class="pinfo"><h4>' + escapeHtml(p.title) + '</h4><p>' + price + '</p></div>'
                + '</a></li>';
            }).join('')
          : '<li class="muted">No products found</li>';
      } catch (err) {
        console.error('search suggest', err);
      }
    }
  }

  /* Duplicate marquee items so the keyframe -50% translate yields
   * a seamless loop regardless of item count. */
  function initMarquees() {
    document.querySelectorAll('.marquee').forEach((marquee) => {
      const track = marquee.querySelector('.marquee__track');
      if (!track) return;
      const clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      marquee.appendChild(clone);
    });
  }

  function initMegaMenu() {
    const root = document.querySelector('[data-mega-menu]');
    if (!root) return;
    const triggers = Array.from(document.querySelectorAll('[data-mega-trigger]'));
    if (!triggers.length) return;
    const panels = Array.from(root.querySelectorAll('[data-mega-panel]'));
    let activeKey = null;
    let closeTimer = null;

    const triggerFor = (key) => triggers.find((t) => t.dataset.megaTrigger === key);
    const panelFor = (key) => panels.find((p) => p.dataset.megaPanel === key);

    /* Anchor the dropdown directly under its nav trigger. Header is sticky,
       so we reposition on scroll/resize to keep it glued while it's open. */
    const positionPanel = (key) => {
      const trigger = triggerFor(key);
      const panel = panelFor(key);
      if (!trigger || !panel) return;
      const r = trigger.getBoundingClientRect();
      const gap = 6;
      const vw = window.innerWidth;
      const pw = panel.offsetWidth || 320;
      let left = r.left;
      if (left + pw > vw - 12) left = Math.max(12, vw - 12 - pw);
      panel.style.top = Math.round(r.bottom + gap) + 'px';
      panel.style.left = Math.round(left) + 'px';
      panel.style.maxHeight = Math.max(180, Math.round(window.innerHeight - r.bottom - gap - 12)) + 'px';
    };

    const open = (key) => {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      activeKey = key;
      root.hidden = false;
      panels.forEach((p) => { p.hidden = p.dataset.megaPanel !== key; });
      triggers.forEach((t) => t.setAttribute('aria-expanded', t.dataset.megaTrigger === key ? 'true' : 'false'));
      positionPanel(key);
    };
    const close = () => {
      activeKey = null;
      root.hidden = true;
      panels.forEach((p) => { p.hidden = true; });
      triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    };
    const scheduleClose = () => {
      if (closeTimer) clearTimeout(closeTimer);
      closeTimer = setTimeout(close, 180);
    };

    triggers.forEach((t) => {
      const key = t.dataset.megaTrigger;
      t.addEventListener('mouseenter', () => open(key));
      t.addEventListener('focus',      () => open(key));
      t.addEventListener('click',      (e) => { e.preventDefault(); activeKey === key ? close() : open(key); });
      t.addEventListener('mouseleave', scheduleClose);
    });
    panels.forEach((p) => {
      p.addEventListener('mouseenter', () => { if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; } });
      p.addEventListener('mouseleave', scheduleClose);
    });

    window.addEventListener('resize', () => { if (activeKey) positionPanel(activeKey); });
    window.addEventListener('scroll', () => { if (activeKey) positionPanel(activeKey); }, { passive: true });

    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && activeKey) close(); });
    document.addEventListener('click', (e) => {
      if (!activeKey) return;
      if (e.target.closest('[data-mega-close]')) { close(); return; }
      if (e.target.closest('[data-mega-trigger]') || e.target.closest('[data-mega-menu]')) return;
      close();
    });
  }

  function initMobileNav() {
    const toggle = document.querySelector('[data-mobile-nav-toggle]');
    const nav = document.querySelector('[data-mobile-nav]');
    if (!toggle || !nav) return;
    toggle.addEventListener('click', () => {
      const open = nav.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(open));
    });
  }

  function initVariantSelectors() {
    document.querySelectorAll('[data-variant-form]').forEach((form) => {
      const variantInput = form.querySelector('[name="id"]');
      const priceEl = form.querySelector('[data-product-price]');
      const variants = JSON.parse(form.dataset.variants || '[]');
      if (!variantInput || !variants.length) return;

      form.querySelectorAll('[data-variant-option]').forEach((btn) => {
        btn.addEventListener('click', () => {
          form.querySelectorAll('[data-variant-option]').forEach((b) => b.classList.remove('is-selected'));
          btn.classList.add('is-selected');
          const variantId = btn.dataset.variantId;
          variantInput.value = variantId;
          const v = variants.find((x) => String(x.id) === String(variantId));
          if (v && priceEl) priceEl.textContent = v.priceFormatted;
        });
      });
    });
  }

  /* =========================================================
   * Cart drawer + AJAX cart
   * Uses Shopify's Section Rendering API to refresh the drawer
   * after add/change/clear without a page reload.
   * ========================================================= */
  function initCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    const open = () => {
      drawer.hidden = false;
      drawer.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => drawer.classList.add('is-open'));
      document.body.classList.add('cart-drawer-open');
    };
    const close = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-drawer-open');
      setTimeout(() => { drawer.hidden = true; }, 300);
    };

    // Toggle from header cart icon
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-toggle]')) {
        e.preventDefault();
        drawer.hidden ? open() : close();
        return;
      }
      if (e.target.closest('[data-cart-close]')) {
        e.preventDefault();
        close();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !drawer.hidden) close();
    });

    // Refresh drawer + header count from Section Rendering API.
    // Falls back to a page reload if section render fails (e.g. theme
    // misconfigured) so the user always sees fresh cart state.
    async function refreshDrawer() {
      try {
        const res = await fetch(withThemeContext('/?sections=cart-drawer'), { headers: { 'Accept': 'application/json' } });
        if (!res.ok) throw new Error('section render http ' + res.status);
        const data = await res.json();
        const html = data['cart-drawer'];
        if (!html) throw new Error('section render empty');
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-cart-drawer]');
        if (!fresh) throw new Error('section render no drawer');
        drawer.innerHTML = fresh.innerHTML;
        const count = parseInt(fresh.dataset.cartCount || '0', 10);
        updateHeaderCount(count);
      } catch (err) {
        // Last-resort fallback: full page reload so cart state is correct
        console.warn('refreshDrawer fell back to reload', err);
        window.location.reload();
      }
    }

    function updateHeaderCount(count) {
      const el = document.querySelector('[data-header-cart-count]');
      if (!el) return;
      el.textContent = count;
      el.hidden = count === 0;
    }

    async function changeLine(key, quantity) {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity }),
        });
        if (!res.ok) throw new Error('change failed');
        await refreshDrawer();
      } catch (err) { console.error('change line', err); }
    }

    async function clearCart() {
      try {
        await fetch('/cart/clear.js', { method: 'POST' });
        await refreshDrawer();
      } catch (err) { console.error('clear cart', err); }
    }

    // Delegate clicks inside drawer (works after innerHTML refresh)
    drawer.addEventListener('click', (e) => {
      const dec = e.target.closest('[data-cart-decrement]');
      const inc = e.target.closest('[data-cart-increment]');
      const rem = e.target.closest('[data-cart-remove]');
      const clr = e.target.closest('[data-cart-clear]');
      if (dec) { e.preventDefault(); changeLine(dec.dataset.cartDecrement, parseInt(dec.dataset.quantity, 10)); }
      else if (inc) { e.preventDefault(); changeLine(inc.dataset.cartIncrement, parseInt(inc.dataset.quantity, 10)); }
      else if (rem) { e.preventDefault(); changeLine(rem.dataset.cartRemove, 0); }
      else if (clr) { e.preventDefault(); clearCart(); }
    });

    // Intercept all "Add to cart" forms — submit via AJAX, open drawer on success
    document.addEventListener('submit', async (e) => {
      const form = e.target;
      if (!form.matches('form[action="/cart/add"], form[action^="/cart/add"]')) return;
      e.preventDefault();
      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;
      try {
        const formData = new FormData(form);
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { 'Accept': 'application/json' },
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          alert(err.description || err.message || 'Could not add to cart');
          return;
        }
        await refreshDrawer();
        open();
      } catch (err) {
        console.error('add to cart', err);
        alert('Could not add to cart');
      } finally {
        if (submitBtn) submitBtn.disabled = false;
      }
    });
  }

  function initCartUpdates() {
    document.querySelectorAll('[data-cart-line-qty]').forEach((input) => {
      input.addEventListener('change', async (e) => {
        const lineKey = input.dataset.lineKey;
        const qty = Math.max(0, parseInt(input.value, 10) || 0);
        try {
          const res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: lineKey, quantity: qty }),
          });
          if (res.ok) window.location.reload();
        } catch (err) {
          console.error('Cart update failed', err);
        }
      });
    });

    document.querySelectorAll('[data-cart-line-remove]').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const lineKey = btn.dataset.lineKey;
        try {
          const res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: lineKey, quantity: 0 }),
          });
          if (res.ok) window.location.reload();
        } catch (err) {
          console.error('Cart remove failed', err);
        }
      });
    });
  }
})();
